import { DEFAULT_STORE_OBJECT } from '@/redux/constants';
import type { DefaultStoreObject } from '@/redux/types';
import type {
  ProcurementLane,
  ProcurementTender,
  ProcurementTenderHistoricalBid,
  ProcurementTenderTransportProvider,
} from '@/views/screens/Procurement';
import ProcurementTenderType from './ProcurementTenderType';
import getTenderCopy from './requestPayload/GetTenderCopy';
import getTenderListBySearchReq from './requestPayload/GetTenderListBySearch';

export interface ProcurementTenderState extends Record<string, any> {
  addNewBidParameterActions: {
    btnCliked: boolean;
  };
  addNewLaneBtnClicked: boolean;
  activeTender?: {
    data: ProcurementTender;
  };
  tenderList: DefaultStoreObject<ProcurementTender[]> & {
    req: Record<string, any>;
    totalModeTest?: number;
    totalModeProd?: number;
    totalCount?: number;
  };
  LaneInformationList: DefaultStoreObject<ProcurementLane[]>;
  transportProviderList: DefaultStoreObject<ProcurementTenderTransportProvider[]> & {
    req?: Record<string, any>;
  };
  historicalBidList: DefaultStoreObject<ProcurementTenderHistoricalBid> & {
    req?: Record<string, any>;
  };
}

const initialState: ProcurementTenderState = {
  tenderList: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    req: {
      ...getTenderListBySearchReq,
    },
  },
  LaneInformationList: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
  },
  transportProviderList: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
  },
  addNewBidParameterActions: {
    btnCliked: false,
  },
  historicalBidList: {
    ...DEFAULT_STORE_OBJECT,
    data: {} as ProcurementTenderHistoricalBid,
  },
  addNewLaneBtnClicked: false,
};

// biome-ignore lint/style/useDefaultParameterLast: <explanation>
const ProcurementTenderReducer = (state = initialState, { type, payload }: any): ProcurementTenderState => {
  switch (type) {
    case ProcurementTenderType.ADD_NEW_LANE_BUTTON: {
      return {
        ...state,
        addNewLaneBtnClicked: payload,
      };
    }
    case ProcurementTenderType.GET_PROCUREMENT_TENDER_LIST: {
      return {
        ...state,
        tenderList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.tenderList,
          req: {
            ...state.tenderList.req,
            ...payload,
          },
          progressing: true,
        },
      };
    }
    case ProcurementTenderType.GET_PROCUREMENT_TENDER_LIST_SUCCESS: {
      return {
        ...state,
        tenderList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.tenderList,
          success: true,
          data: [...(state?.tenderList?.data || []), ...(payload?.data?.tenderViewModel || [])],
          totalModeTest: payload?.data?.totalModeTest,
          totalModeProd: payload?.data?.totalModeProd,
          totalCount: payload?.data?.totalCount,
          progressing: false,
        },
      };
    }

    case ProcurementTenderType.GET_PROCUREMENT_TENDER_LIST_FAIL: {
      return {
        ...state,
        tenderList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.tenderList,
          error: true,
          data: payload.data,
          progressing: false,
          totalModeTest: 0,
          totalModeProd: 0,
          totalCount: 0,
        },
      };
    }

    case ProcurementTenderType.UPDATE_PROCUREMENT_TENDER_INFO_REQUEST:
      return {
        ...state,
        req: {
          ...state.getTenderListBySearchReq,
          ...payload,
        },
      };

    case ProcurementTenderType.GET_PROCUREMENT_TENDER_LIST_REQUEST_RESET:
      return {
        ...state,
        tenderList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.tenderList,
          req: {
            ...state.tenderList.req,
            ...payload,
          },
          data: [],
        },
      };

    /** Active Tender Item set and reset reducer start */

    case ProcurementTenderType.SET_ACTIVE_TENDER_ITEM:
      return {
        ...state,
        activeTender: {
          data: payload,
        },
      };

    case ProcurementTenderType.RESET_ACTIVE_TENDER_ITEM:
      return {
        ...state,
        activeTender: {
          data: {} as ProcurementTender,
        },
      };
    /** Active Tender Item set and reset reducer end */

    /**Tender Copy reducer start */
    case ProcurementTenderType.CREATE_TENDER_COPY:
      return {
        ...state,
        tenderCopy: {
          ...DEFAULT_STORE_OBJECT,
          req: {
            ...state.tenderCopy.req,
            ...payload,
          },
          progressing: true,
        },
      };

    case ProcurementTenderType.CREATE_TENDER_COPY_SUCCESS:
      return {
        ...state,
        tenderCopy: {
          ...DEFAULT_STORE_OBJECT,
          ...state.tenderCopy,
          error: false,
          success: true,
          data: payload.data,
          progressing: false,
        },
      };

    case ProcurementTenderType.CREATE_TENDER_COPY_FAIL:
      return {
        ...state,
        tenderCopy: {
          ...DEFAULT_STORE_OBJECT,
          ...state.tenderCopy,
          error: true,
          data: payload,
          success: false,
          progressing: false,
        },
      };

    case ProcurementTenderType.CREATE_TENDER_COPY_RESET:
      return {
        ...state,
        tenderCopy: {
          ...DEFAULT_STORE_OBJECT,
          ...state.tenderCopy,
          req: {
            ...getTenderCopy,
          },
          success: false,
          error: false,
          data: [],
        },
      };
    /** Tender Copy reducer  end */

    case ProcurementTenderType.ADD_NEW_BID_PARAMETER_ACTION:
      return {
        ...state,
        addNewBidParameterActions: {
          btnCliked: payload,
        },
      };

    case ProcurementTenderType.GET_PROCUREMENT_LANE_INFORMATION:
      return {
        ...state,
        LaneInformationList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.LaneInformationList,
          progressing: true,
        },
      };

    case ProcurementTenderType.GET_PROCUREMENT_LANE_INFORMATION_SUCCESS:
      return {
        ...state,
        LaneInformationList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.LaneInformationList,
          success: true,
          data: payload.data,
          progressing: false,
        },
      };

    case ProcurementTenderType.GET_PROCUREMENT_LANE_INFORMATION_FAIL:
      return {
        ...state,
        LaneInformationList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.LaneInformationList,
          error: true,
          data: payload.data,
          progressing: false,
        },
      };

    case ProcurementTenderType.GET_PROCUREMENT_LANE_INFORMATION_REQUEST_RESET:
      return {
        ...state,
        LaneInformationList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.LaneInformationList,
          data: [],
        },
      };

    case ProcurementTenderType.GET_PROCUREMENT_ADD_LANE_INFORMATION:
      return {
        ...state,
        AddLaneInformationList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.AddLaneInformationList,
          progressing: true,
        },
      };

    case ProcurementTenderType.GET_PROCUREMENT_ADD_LANE_INFORMATION_SUCCESS:
      return {
        ...state,
        AddLaneInformationList: {
          ...state.AddLaneInformationList,
          success: true,
          data: payload.data.data,
          progressing: false,
        },
      };

    case ProcurementTenderType.GET_PROCUREMENT_ADD_LANE_INFORMATION_FAIL:
      return {
        ...state,
        AddLaneInformationList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.AddLaneInformationList,
          error: true,
          data: payload?.data,
          progressing: false,
          totalActive: 0,
          totalApproved: 0,
          totalCount: 0,
        },
      };

    case ProcurementTenderType.GET_PROCUREMENT_ADD_LANE_INFORMATION_REQUEST_RESET:
      return {
        ...state,
        AddLaneInformationList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.AddLaneInformationList,
          req: {
            ...state.AddLaneInformationList.req,
            payload,
          },
          data: [],
        },
      };

    case ProcurementTenderType.GET_TENDER_CARRIER_LIST:
      return {
        ...state,
        transportProviderList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.transportProviderList,

          progressing: true,
        },
      };

    case ProcurementTenderType.GET_TENDER_CARRIER_LIST_SUCCESS:
      return {
        ...state,
        transportProviderList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.transportProviderList,
          success: true,
          data: payload.data,
          progressing: false,
        },
      };

    case ProcurementTenderType.GET_TENDER_CARRIER_LIST_FAIL:
      return {
        ...state,
        transportProviderList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.transportProviderList,
          error: true,
          data: payload.data,
          progressing: false,
        },
      };

    case ProcurementTenderType.GET_TENDER_CARRIER_LIST_REQUEST_RESET:
      return {
        ...state,
        transportProviderList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.transportProviderList,
          req: {
            ...state.transportProviderList.req,
            payload,
          },
          data: [],
        },
      };

    case ProcurementTenderType.GET_PROCUREMENT_HISTORICAL_BID_LIST:
      return {
        ...state,
        historicalBidList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.historicalBidList,
          req: {
            ...state.historicalBidList.req,
            ...payload,
          },
          progressing: true,
        },
      };

    case ProcurementTenderType.GET_PROCUREMENT_HISTORICAL_BID_LIST_SUCCESS:
      return {
        ...state,
        historicalBidList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.historicalBidList,
          success: true,
          data: payload.data,
          progressing: false,
        },
      };

    case ProcurementTenderType.GET_PROCUREMENT_HISTORICAL_BID_LIST_FAIL:
      return {
        ...state,
        historicalBidList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.historicalBidList,
          error: true,
          data: payload.data,
          progressing: false,
        },
      };

    case ProcurementTenderType.GET_PROCUREMENT_HISTORICAL_BID_LIST_REQUEST_RESET:
      return {
        ...state,
        historicalBidList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.historicalBidList,
          req: {
            // @ts-ignore
            ...state?.historicalBidList?.Req,
            ...payload,
          },
          data: {} as ProcurementTenderHistoricalBid,
        },
      };

    default:
      return state;
  }
};

export default ProcurementTenderReducer;
